import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Combobox } from "@/components/ui/combobox";
import { useToast } from "@/components/ui/use-toast";
import { DuplicateTemplateValidation, useDuplicateTemplate } from "@/lib/api/cfr";
import { CommodityOptions } from "@/lib/api/commodity";
import { useListCustomers } from "@/lib/api/customer";

interface DuplicateTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  defaultCommodity?: string;
  defaultCropYear?: number;
  defaultDestinationId?: string;
  onSuccess: () => void;
}

interface DuplicateFormData {
  title?: string;
  crop_year?: number;
  customer_destination_id?: string;
  commodity?: string;
}

export function DuplicateTemplateDialog({ 
  open, 
  onOpenChange, 
  templateId,
  defaultCommodity,
  defaultCropYear,
  defaultDestinationId,
  onSuccess 
}: DuplicateTemplateDialogProps) {
  const { toast } = useToast();
  const duplicateTemplate = useDuplicateTemplate();
  const customersResult = useListCustomers({ minimal: true });

  const [formData, setFormData] = useState<DuplicateFormData>({
    commodity: defaultCommodity,
    crop_year: defaultCropYear,
    customer_destination_id: defaultDestinationId,
    title: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const customers = customersResult?.data?.data || [];
  const customerOptions = customers.map((customer: { id: string; long_name: string }) => ({
    value: customer.id,
    label: customer.long_name
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const parseResult = DuplicateTemplateValidation.safeParse({
        template_id: templateId,
        crop_year: formData.crop_year,
        customer_destination_id: formData.customer_destination_id,
        commodity: formData.commodity,
        title: formData.title?.trim(),
      });

      if (!parseResult.success) {
        console.error("failed to parse api body", parseResult.error);
        toast({
          title: "Error",
          description: "Invalid data",
          variant: "destructive",
        });
        return;
      }

      const apiResult = await duplicateTemplate(parseResult.data);
      if (!apiResult.success) {
        throw apiResult;
      }

      toast({
        title: "Template Duplicated",
        description: "Successfully created a copy of the template",
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to duplicate template",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Duplicate CFR Template</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="commodity">Commodity (Optional)</Label>
              <Combobox
                className="w-full"
                popoverClassName="min-w-[400px]"
                options={CommodityOptions}
                value={formData.commodity || ""}
                onValueChange={(value) => setFormData(prev => ({ ...prev, commodity: value || undefined }))}
                placeholder="Keep original or select new..."
                dwimOnChange
                isModal
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="crop_year">Crop Year (Optional)</Label>
              <Input
                id="crop_year"
                type="number"
                value={formData.crop_year || ""}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  crop_year: e.target.value ? parseInt(e.target.value) : undefined 
                }))}
                placeholder="Keep original or enter new..."
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="customer_destination_id">Destination (Optional)</Label>
              <Combobox
                className="w-full"
                popoverClassName="min-w-[450px]"
                options={customerOptions}
                value={formData.customer_destination_id || ""}
                onValueChange={(value) => setFormData(prev => ({ ...prev, customer_destination_id: value || undefined }))}
                placeholder="Keep original or select new..."
                virtualizeList
                dwimOnChange
                isModal
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="title">Title (Optional)</Label>
              <Input
                id="title"
                value={formData.title || ""}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  title: e.target.value ? e.target.value : undefined 
                }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Duplicating..." : "Duplicate Template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
